import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Field, FieldGroup, FieldLabel, FieldDescription } from '@/components/ui/field';
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { usePermissions } from '@/features/permissions';
import { usePatientConfiguration, useSavePatientConfiguration, type PatientConfiguration } from './api';
export function MrnSettings() {
  const config = usePatientConfiguration();
  const { can } = usePermissions();
  if (!can('manage_organization')) return null;
  if (config.isError) return <p role="alert">Patient settings could not be loaded.</p>;
  if (!config.data) return <p>Loading MRN preferences…</p>;
  return <Editor key={JSON.stringify(config.data)} config={config.data} />;
}
function Editor({ config }: { config: PatientConfiguration }) {
  const [mrn, setMrn] = useState(config.mrn);
  const save = useSavePatientConfiguration();
  return (
    <FieldGroup>
      <h3 className="font-medium">MRN assignment</h3>
      <Field>
        <FieldLabel>Assignment preference</FieldLabel>
        <Select value={mrn.mode} onValueChange={(mode) => setMrn({ ...mrn, mode: mode as typeof mrn.mode })}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              <SelectItem value="manual">Create your own</SelectItem>
              <SelectItem value="random">Random MRN</SelectItem>
              <SelectItem value="rule">Practice rule</SelectItem>
            </SelectGroup>
          </SelectContent>
        </Select>
        <FieldDescription>
          Applies when a new patient is created without an MRN. Existing MRNs and explicitly supplied imported MRNs are
          preserved.
        </FieldDescription>
      </Field>
      {mrn.mode === 'rule' && (
        <>
          <Field>
            <FieldLabel htmlFor="mrn-prefix">Prefix</FieldLabel>
            <Input
              id="mrn-prefix"
              value={mrn.prefix}
              maxLength={20}
              pattern="[A-Za-z0-9-]*"
              onChange={(e) => setMrn({ ...mrn, prefix: e.target.value })}
            />
          </Field>
          <Field>
            <FieldLabel>Include year</FieldLabel>
            <Select
              value={String(mrn.include_year)}
              onValueChange={(v) => setMrn({ ...mrn, include_year: v === 'true' })}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectItem value="false">No</SelectItem>
                  <SelectItem value="true">Yes — practice local year</SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select>
          </Field>
          <Field>
            <FieldLabel htmlFor="mrn-digits">Minimum sequence digits</FieldLabel>
            <Input
              id="mrn-digits"
              type="number"
              min={3}
              max={12}
              value={mrn.digits}
              onChange={(e) => setMrn({ ...mrn, digits: Number(e.target.value) })}
            />
          </Field>
          <Field>
            <FieldLabel htmlFor="mrn-next">Next sequence number</FieldLabel>
            <Input
              id="mrn-next"
              type="number"
              min={config.mrn.next_number}
              value={mrn.next_number}
              onChange={(e) => setMrn({ ...mrn, next_number: Number(e.target.value) })}
            />
            <FieldDescription>
              Sequence numbers continue across years. Existing MRNs are skipped; the sequence cannot be moved backward.
            </FieldDescription>
          </Field>
          <p>
            Example: {mrn.prefix}
            {mrn.include_year ? 'YYYY' : ''}
            {String(mrn.next_number).padStart(Math.min(12, Math.max(3, mrn.digits)), '0')}
          </p>
        </>
      )}
      <Button
        className="self-start"
        disabled={save.isPending}
        onClick={async () => {
          try {
            await save.mutateAsync({ ...config, mrn });
            toast.success('MRN preferences saved');
          } catch {
            toast.error('Unable to save MRN preferences. Check the rule values.');
          }
        }}>
        Save MRN preferences
      </Button>
    </FieldGroup>
  );
}
